import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button, Spinner, Alert } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../components/Forms/PhoneInput";
import "../../assets/css/page-common.css";
import BackButton from "../../components/Common/BackButton";

const NAVY = "#221C5D";

export default function EditUserPage() {
    const { id: userId } = useParams();
    const navigate = useNavigate();

    const [userType, setUserType] = useState("company"); // "company" | "account" | "super"
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const toTitleCaseTR = (s) =>
        s.toLocaleLowerCase("tr-TR").replace(/(^|[\s\-'])(\p{L})/gu, (_, p1, p2) => p1 + p2.toLocaleUpperCase("tr-TR"));

    const [form, setForm] = useState({
        phoneNumber: "",
        balance: 0,
        companyId: "",
        accountId: "",
        username: "",
        email: "",
        password: "",
        roles: [],
        twoFaEnabled: false,
        enabled: true,
    });

    useEffect(() => {
        document.title = "Kullanıcı Düzenle | Tahsilat360";
    }, []);

    // me, şirketler, cariler ve kullanıcı detaylarını çek
    useEffect(() => {
        const bootstrap = async () => {
            try {
                setLoading(true);

                // paralel istekler
                const [meRes, compRes, accRes, userRes] = await Promise.all([
                    axios.get(API.auth_me),
                    axios.get(API.get_companies_names),
                    axios.get(API.get_accounts_id_name),
                    axios.get(API.admin_user(userId)), // GET kullanıcı detayı (PUT ile aynı endpoint varsayıldı)
                ]);

                setUserRoles(meRes.data?.data?.roles || []);
                setCompanies(compRes.data?.data || []);
                setAccounts(accRes.data?.data || []);

                const u = userRes.data?.data;
                if (!u) throw new Error("Kullanıcı bulunamadı");

                // kullanıcı tipini tespit et
                let detectedType = "company";
                if (u.roles?.includes("ROLE_SUPER_ADMIN")) detectedType = "super";
                else if (u.accountId) detectedType = "account";
                setUserType(detectedType);

                setForm({
                    phoneNumber: u.phoneNumber || "",
                    balance: u.balance ?? 0,
                    companyId: u.companyId || "",
                    accountId: u.accountId || "",
                    username: u.username || "",
                    email: u.email || "",
                    password: "",
                    roles: u.roles || [],
                    twoFaEnabled: u.twoFaEnabled || false,
                    enabled: u.enabled !== undefined ? u.enabled : true,
                });
            } catch (e) {
                console.error(e);
                setError("Kullanıcı detayları yüklenemedi");
            } finally {
                setLoading(false);
            }
        };
        bootstrap();
    }, [userId, navigate]);

    const getTahsilat360Id = () => {
        const t = companies.find(c => ["tahsilat360", "tahsilatcepte"].includes(c.companyName?.toLowerCase()));
        return t ? t.id : "";
    };

    const hasRole = (req) => !req?.length || req.some(r => userRoles.includes(r));
    const canCreateSuperAdmin = () => hasRole(["ROLE_SUPER_ADMIN"]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleRoleToggle = (role) => {
        setForm(prev => ({ ...prev, roles: [role] }));
    };

    const handleUserTypeChange = (newType) => {
        setUserType(newType);
        setForm(prev => ({
            ...prev,
            companyId: newType === "super" ? getTahsilat360Id() : (newType === "company" ? prev.companyId : ""),
            accountId: newType === "account" ? prev.accountId : "",
            roles: [],
        }));
    };

    const getAllowedRoles = () => {
        if (userType === "account") return ["ROLE_ACCOUNT_ADMIN", "ROLE_ACCOUNT_ACCOUNTANT"];
        if (userType === "company") return ["ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER"];
        if (userType === "super") return ["ROLE_SUPER_ADMIN"];
        return [];
    };
    const allowedRoles = getAllowedRoles();

    const roleDisplayNames = {
        ROLE_ACCOUNT_ADMIN: "CARİ/BAYİ ADMIN",
        ROLE_ACCOUNT_ACCOUNTANT: "CARİ/BAYİ MUHASEBE",
        ROLE_COMPANY_ADMIN: "ŞİRKET ADMIN",
        ROLE_COMPANY_ACCOUNTANT: "ŞİRKET MUHASEBE",
        ROLE_COMPANY_SALESPERSON: "ŞİRKET SATIŞ",
        ROLE_COMPANY_CASHIER: "ŞİRKET KASİYER",
        ROLE_SUPER_ADMIN: "SUPER ADMIN",
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);

        if (!form.username.trim()) { toast.error("Ad soyad gereklidir"); setSaving(false); return; }
        if (!form.email.trim()) { toast.error("E-posta gereklidir"); setSaving(false); return; }
        if (userType === "company" && !form.companyId) { toast.error("Şirket seçimi gereklidir"); setSaving(false); return; }
        if (userType === "account" && !form.accountId) { toast.error("Cari/Bayi hesap seçimi gereklidir"); setSaving(false); return; }
        if (userType === "super" && !form.companyId) { toast.error("Super admin için Tahsilat360 şirketi seçilmelidir"); setSaving(false); return; }
        if (form.roles.length === 0) { toast.error("Bir rol seçilmelidir"); setSaving(false); return; }

        try {
            const payload = {
                phoneNumber: form.phoneNumber || undefined,
                balance: form.balance,
                username: form.username,
                email: form.email,
                password: form.password?.trim() === "" ? null : form.password,
                roles: form.roles,
                twoFaEnabled: form.twoFaEnabled,
                enabled: form.enabled,
            };
            if (userType === "company" || userType === "super") {
                payload.companyId = form.companyId; payload.accountId = null;
            } else if (userType === "account") {
                payload.accountId = form.accountId; payload.companyId = null;
            }

            await axios.put(API.admin_user(userId), payload, {
                headers: { "Content-Type": "application/json" },
            });
            toast.success("Kullanıcı başarıyla güncellendi!");
            navigate("/users");
        } catch (err) {
            const msg = err.response?.data?.message || "Kullanıcı güncellenirken bir hata oluştu";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="container-fluid text-center p-5">
                    <Spinner className="me-2" /> Kullanıcı bilgileri yükleniyor...
                </div>
            </div>
        );
    }

    return (
        <div className="page-content page-common">
            <div className="container-fluid">

                {/* ── Page Header ── */}
                <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <BackButton />
                        <div>
                            <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                {form.username || "Kullanıcı Düzenle"}
                            </h5>
                            <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                Kullanıcı Düzenleme Sayfası
                            </span>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button
                            className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                            style={{ height: 34, fontSize: ".8rem" }}
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Değişiklikleri Kaydet</>)}
                        </button>
                    </div>
                </div>

                <Row>
                    <Col lg={12}>
                        <Card className="detail-card">
                            <CardHeader>
                                <div className="section-title">
                                    <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                        <i className="bx bx-edit-alt" />
                                    </div>
                                    Kullanıcı Bilgileri
                                </div>
                            </CardHeader>
                            <CardBody className="p-4">
                                {error && (
                                    <Alert color="danger" className="border-0 rounded-4">
                                        <i className="bx bx-error me-2" />
                                        {error}
                                    </Alert>
                                )}

                                <Form>
                                    {/* Kullanıcı Tipi */}
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">Kullanıcı Tipi</h6>
                                        <div className="d-flex gap-3 mt-2 flex-wrap">
                                            {[
                                                { key: "company", title: "Firma Kullanıcısı", desc: "Şirket bünyesinde çalışan kullanıcı" },
                                                { key: "account", title: "Cari/Bayi Kullanıcı", desc: "Cari/Bayi hesaba bağlı harici kullanıcı" },
                                                ...(canCreateSuperAdmin() ? [{ key: "super", title: "Super Admin", desc: "Sistem yöneticisi kullanıcı" }] : []),
                                            ].map(opt => (
                                                <div
                                                    key={opt.key}
                                                    className={`p-3 border rounded d-flex align-items-center ${userType === opt.key ? "border-primary bg-light" : "border-secondary"}`}
                                                    style={{ cursor: "pointer", minWidth: 180 }}
                                                    onClick={() => handleUserTypeChange(opt.key)}
                                                >
                                                    <div
                                                        className="me-3"
                                                        style={{
                                                            width: 20, height: 20, borderRadius: "50%",
                                                            border: `2px solid ${userType === opt.key ? "#007bff" : "#6c757d"}`,
                                                            backgroundColor: userType === opt.key ? "#007bff" : "transparent",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {userType === opt.key && (
                                                            <div style={{
                                                                width: 8, height: 8, borderRadius: "50%", backgroundColor: "white",
                                                                position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                                                            }}/>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">{opt.title}</div>
                                                        <small className="text-muted">{opt.desc}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Telefon */}
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">İletişim</h6>
                                        <FormGroup className="mb-0">
                                            <PhoneInput value={form.phoneNumber} onChange={handleChange} name="phoneNumber" />
                                        </FormGroup>
                                    </div>

                                    {/* İlişkilendirme */}
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">İlişkilendirme</h6>
                                        {userType === "company" && (
                                            <FormGroup>
                                                <Label>Firma *</Label>
                                                <Input
                                                    type="select"
                                                    name="companyId"
                                                    value={form.companyId}
                                                    onChange={handleChange}
                                                    disabled={loadingCompanies}
                                                    className="rounded-3"
                                                >
                                                    <option value="">{loadingCompanies ? "Firmalar yükleniyor..." : "Firma seçin"}</option>
                                                    {companies.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                                                </Input>
                                            </FormGroup>
                                        )}
                                        {userType === "super" && (
                                            <FormGroup>
                                                <Label>Firma *</Label>
                                                <Input
                                                    type="select"
                                                    name="companyId"
                                                    value={getTahsilat360Id()}
                                                    disabled
                                                    className="rounded-3"
                                                    style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                                                >
                                                    <option value={getTahsilat360Id()}>Tahsilat360 (Otomatik Seçili)</option>
                                                </Input>
                                                <small className="text-muted">Super Admin kullanıcıları otomatik olarak Tahsilat360 şirketine atanır</small>
                                            </FormGroup>
                                        )}
                                        {userType === "account" && (
                                            <FormGroup>
                                                <Label>Cari/Bayi Hesap *</Label>
                                                <Input
                                                    type="select"
                                                    name="accountId"
                                                    value={form.accountId}
                                                    onChange={handleChange}
                                                    disabled={loadingAccounts}
                                                    className="rounded-3"
                                                >
                                                    <option value="">{loadingAccounts ? "Cari/Bayi hesaplar yükleniyor..." : "Cari/Bayi hesap seçin"}</option>
                                                    {accounts.map(a => <option key={a.id} value={a.id}>{a.accountName}</option>)}
                                                </Input>
                                            </FormGroup>
                                        )}
                                    </div>

                                    {/* Kimlik Bilgileri */}
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">Kullanıcı Bilgileri</h6>
                                        <FormGroup>
                                            <Label>Ad Soyad *</Label>
                                            <Input
                                                name="username"
                                                value={form.username}
                                                onChange={(e) => setForm(prev => ({ ...prev, username: toTitleCaseTR(e.target.value) }))}
                                                maxLength={50}
                                                placeholder="Ad soyad girin"
                                                className="rounded-3"
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>E-posta *</Label>
                                            <Input
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                maxLength={100}
                                                placeholder="E-posta adresi girin"
                                                className="rounded-3"
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>Yeni Parola</Label>
                                            <Input
                                                type="password"
                                                name="password"
                                                value={form.password}
                                                onChange={handleChange}
                                                placeholder="Yeni parola girin (boş bırakılırsa değiştirilmez)"
                                                className="rounded-3"
                                            />
                                            <small className="text-muted">Boş bırakılırsa mevcut parola korunur</small>
                                        </FormGroup>
                                    </div>

                                    {/* Roller & Durum */}
                                    <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: "#f9f9f9" }}>
                                        <h6 className="mb-3 fw-semibold">Rol ve Durum</h6>
                                        <FormGroup>
                                            <Label>Roller *</Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {allowedRoles.map((role) => (
                                                    <Button
                                                        key={role}
                                                        color={form.roles.includes(role) ? "primary" : "secondary"}
                                                        size="sm"
                                                        outline={!form.roles.includes(role)}
                                                        onClick={(e) => { e.preventDefault(); handleRoleToggle(role); }}
                                                        className="rounded-pill"
                                                    >
                                                        {roleDisplayNames[role] || role}
                                                    </Button>
                                                ))}
                                            </div>
                                            <small className="text-muted">
                                                {userType === "account" ? "Cari/Bayi kullanıcılar için mevcut roller"
                                                    : userType === "company" ? "Firma kullanıcıları için mevcut roller"
                                                        : "Super Admin rolü"}
                                            </small>
                                        </FormGroup>

                                        {/* 2FA */}
                                        <FormGroup className="mb-3">
                                            <Label>İki Faktörlü Doğrulama</Label>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="toggle-switch"
                                                    onClick={() => setForm(prev => ({ ...prev, twoFaEnabled: !prev.twoFaEnabled }))}
                                                    style={{
                                                        width: 50, height: 24, backgroundColor: form.twoFaEnabled ? "#007bff" : "#6c757d",
                                                        borderRadius: 12, position: "relative", cursor: "pointer", transition: "background-color .2s",
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 20, height: 20, backgroundColor: "#fff", borderRadius: "50%",
                                                        position: "absolute", top: 2, left: form.twoFaEnabled ? 28 : 2, transition: "left .2s",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,.2)",
                                                    }}/>
                                                </div>
                                                <span className="ms-3">
                          <strong>{form.twoFaEnabled ? "Etkin" : "Pasif"}</strong><br/>
                          <small className="text-muted">{form.twoFaEnabled ? "Ekstra güvenlik aktif" : "Sadece şifre ile doğrulama"}</small>
                        </span>
                                            </div>
                                        </FormGroup>

                                        {/* Enabled */}
                                        <FormGroup className="mb-0">
                                            <Label>Kullanıcı Aktif/Pasif</Label>
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="toggle-switch"
                                                    onClick={() => setForm(prev => ({ ...prev, enabled: !prev.enabled }))}
                                                    style={{
                                                        width: 50, height: 24, backgroundColor: form.enabled ? "#28a745" : "#6c757d",
                                                        borderRadius: 12, position: "relative", cursor: "pointer", transition: "background-color .2s",
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 20, height: 20, backgroundColor: "#fff", borderRadius: "50%",
                                                        position: "absolute", top: 2, left: form.enabled ? 28 : 2, transition: "left .2s",
                                                        boxShadow: "0 2px 4px rgba(0,0,0,.2)",
                                                    }}/>
                                                </div>
                                                <span className="ms-3">
                          <strong>{form.enabled ? "Aktif" : "Pasif"}</strong><br/>
                          <small className="text-muted">{form.enabled ? "Giriş yapabilir" : "Devre dışı"}</small>
                        </span>
                                            </div>
                                        </FormGroup>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2">
                                        <button type="button" className="btn btn-light btn-rounded px-4" style={{ height: 34, fontSize: ".8rem" }} onClick={() => navigate(-1)} disabled={saving}>
                                            İptal
                                        </button>
                                        <button type="button" className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1" style={{ height: 34, fontSize: ".8rem" }} onClick={handleSubmit} disabled={saving}>
                                            {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Değişiklikleri Kaydet</>)}
                                        </button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}